import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows after the site navigation and main buy buttons leave the viewport.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #headerIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #buyButtonsInViewport = true;

  /** @type {boolean} */
  #headerInViewport = false;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('change', this.#handleInputChange, { signal });

    this.#getInitialQuantity();
    this.#syncFromBuyButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#headerIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Watches the main buy buttons, site navigation, and footer visibility.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    // Support both legacy `buy-buttons-block` and the newer `spx-buy-buttons-block`.
    const buyButtonsBlock =
      productForm.closest('.buy-buttons-block') ?? productForm.closest('.spx-buy-buttons-block');
    if (!buyButtonsBlock) return;

    const header = document.querySelector('#header-component');
    // In themes migrated from 2.0, the footer element doesn't exist.
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    this.#buyButtonsInViewport = this.#isElementInViewport(buyButtonsBlock);
    this.#headerInViewport = header ? this.#isElementInViewport(header) : false;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      this.#buyButtonsInViewport = entry.isIntersecting;
      this.#updateVisibility();
    });

    if (header) {
      this.#headerIntersectionObserver = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;

        this.#headerInViewport = entry.isIntersecting;
        this.#updateVisibility();
      });
    }

    // Footer isn't available in some migrated themes, so make it optional.
    if (footer) {
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          this.#hiddenByBottom = entry.isIntersecting;
          this.#updateVisibility();
        },
        {
          rootMargin: '200px 0px 0px 0px',
        }
      );
    }

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    if (header) {
      this.#headerIntersectionObserver?.observe(header);
    }
    if (footer) {
      this.#mainBottomObserver?.observe(footer);
    }

    this.#updateVisibility();
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Shows the sticky bar only after both the navigation and main buy buttons leave the viewport.
   */
  #updateVisibility() {
    const shouldShow = !this.#buyButtonsInViewport && !this.#headerInViewport && !this.#hiddenByBottom;

    if (shouldShow && !this.#isStuck) {
      this.#showStickyBar();
    } else if (!shouldShow && this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  /**
   * Checks an element's current geometric visibility before observer callbacks run.
   * @param {Element} element
   * @returns {boolean}
   */
  #isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return rect.top < viewportHeight && rect.bottom > 0 && rect.left < viewportWidth && rect.right > 0;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    // `dataset` values are always strings; event data may be number or string.
    // Use string coercion to avoid strict mismatch and skipping updates.
    const eventProductId = event.detail.data.productId;
    if (String(eventProductId) !== String(this.dataset.productId)) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    let variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    // Fallback: if attribute is missing for some reason, use resource availability.
    if (variantAvailable == null) {
      variantAvailable = variant?.available;
    }
    // Normalize to the CSS expected values: 'true' | 'false'
    this.dataset.variantAvailable = variantAvailable === true || variantAvailable === 'true' ? 'true' : 'false';

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#syncFromBuyButtons();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    this.dataset.variantAvailable = 'false';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    const puppetWasTrue = this.#targetAddToCartButton?.dataset.puppet === 'true';

    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    // Only handle cart adds coming from the main product form
    const eventProductId = event?.detail?.data?.productId;
    const source = event?.detail?.data?.source;
    if (!eventProductId || source !== 'product-form-component') return;
    if (String(eventProductId) !== String(this.dataset.productId)) return;

    // If sticky was the source of the add-to-cart, it already played the animation.
    if (puppetWasTrue) return;

    // Mirror the add-to-cart "added" state on the sticky button.
    if (this.refs.addToCartButton?.dataset?.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
    this.#syncPriceFromBuyButtons();
  };

  /**
   * Handles input changes from bundle checkboxes/quantity inputs.
   * @param {Event} event
   */
  #handleInputChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (
      target.closest('.spx-bundle-products__checkbox') ||
      target.closest('.spx-bundle-products input[name="quantity"]') ||
      target.closest('.spx-warranty-product__checkbox')
    ) {
      this.#syncPriceFromBuyButtons();
    }
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#hiddenByBottom = false;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    // Prefer querying within the current section wrapper directly.
    // This avoids relying on section id formatting differences across theme instances.
    const productFormInSection = sectionElement.querySelector(
      `product-form-component[data-product-id="${productId}"]`
    );
    if (productFormInSection) return /** @type {HTMLElement} */ (productFormInSection);

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #syncFromBuyButtons() {
    this.#syncButtonFromBuyButtons();
    this.#syncPriceFromBuyButtons();
  }

  #syncButtonFromBuyButtons() {
    const productForm = this.#getProductForm();
    const stickyButton = this.refs.addToCartButton;
    if (!productForm || !stickyButton) return;

    const mainButton = productForm.querySelector('[ref="addToCartButton"]');
    if (!(mainButton instanceof HTMLButtonElement)) return;

    stickyButton.disabled = mainButton.disabled;

    const stickyLabel = stickyButton.querySelector('.add-to-cart-text__content > span');
    const mainLabel = mainButton.querySelector('.add-to-cart-text__content > span');
    if (stickyLabel && mainLabel) {
      stickyLabel.textContent = mainLabel.textContent;
    }
  }

  #syncPriceFromBuyButtons() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const sourcePrice = productForm.querySelector('.spx-buy-buttons__price');
    const stickyPriceContainer = this.querySelector('.sticky-add-to-cart__price');
    if (!sourcePrice || !stickyPriceContainer) return;

    const currentPrice = sourcePrice.querySelector('.spx-buy-buttons__price-current')?.textContent?.trim();
    const comparePrice = sourcePrice.querySelector('.spx-buy-buttons__price-compare')?.textContent?.trim();

    if (!currentPrice) return;

    let nextHtml = `<span class="price">${currentPrice}</span>`;
    if (comparePrice) {
      nextHtml += `<span class="compare-at-price">${comparePrice}</span>`;
    }

    stickyPriceContainer.innerHTML = nextHtml;
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
